import { Matrix4 } from 'three'
import { TilesetJsonParser } from '@/plugins/tiles/adapters/TilesetJsonParser'
import type {
  IAssetSource,
  ITileContentAdapter,
  ResourceContext,
  TileContentLease,
  TileContentPackage,
  TileContentPrepareContext,
  TileReadOptions,
  TileRenderItem,
  TileResourceCosts,
  TileResourceData,
  TilesDisplaySnapshot,
  TilesetDocument,
  TilesViewSnapshot,
  TileTextureQualityUpdate,
} from '@/plugins/tiles/contracts'
import type { RuntimeTileNode } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'
import { ExplicitHierarchyProvider } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'
import { resolveTilesRuntimeOptions } from '@/plugins/tiles/runtime/options'
import { TileAdaptiveBudgetController } from '@/plugins/tiles/runtime/TileAdaptiveBudgetController'
import { TileBudgetController } from '@/plugins/tiles/runtime/TileBudgetController'
import {
  TileCache,
  type TileMemoryBytes,
  tileCostBytes,
  tileMemoryCosts,
} from '@/plugins/tiles/runtime/TileCache'
import type { TileCacheBudget } from '@/plugins/tiles/runtime/TileCacheBudget'
import { TileContentRegistry } from '@/plugins/tiles/runtime/TileContentRegistry'
import { TileCostHistory } from '@/plugins/tiles/runtime/TileCostHistory'
import type { TilePreparationCoordinator } from '@/plugins/tiles/runtime/TilePreparationCoordinator'
import { glbByteLength, TileProbeHints } from '@/plugins/tiles/runtime/TileProbeHints'
import { TileScheduler } from '@/plugins/tiles/runtime/TileScheduler'
import type {
  TileSelectionRequest,
  TileSelectionResult,
} from '@/plugins/tiles/runtime/TileSelector'
import { TileSelector } from '@/plugins/tiles/runtime/TileSelector'
import { TileTextureQualityController } from '@/plugins/tiles/runtime/TileTextureQualityController'
import { classifyTileResource } from '@/plugins/tiles/sources/classifyTileResource'
import type { TilesDiagnostic } from '@/plugins/tiles/TilesError'
import { TilesError, throwIfTilesAborted, toTilesDiagnostic } from '@/plugins/tiles/TilesError'
import type {
  TilesAdaptiveBudgetOptions,
  TilesBudgetPressure,
  TilesPipelineStatus,
  TilesRuntimeOptions,
} from '@/plugins/tiles/types'
import type { PhaseRenderContext, RenderPhase } from '@/shared/types/assets'
import type { PickQuery, PickResult } from '@/shared/types/core'
import type { ViewFrameContext } from '@/shared/types/view'

let nextRecordGeneration = 0

interface QualityJob {
  readonly id: string
  readonly reservationId: string
  readonly controller: AbortController
  readonly content: TileContentPackage
  readonly lease: TileContentLease
  readonly target: number
  readonly generation: number
  readonly rendererGeneration: number
  readonly version: number
  update?: TileTextureQualityUpdate
  releaseOutput?: () => void
  ready: boolean
  finished: boolean
}

/** 一份 Record 的流式状态；异步回调只提交资源，显示集合只在 update 的帧边界切换。 */
export class TilesRuntime {
  readonly hierarchy: ExplicitHierarchyProvider
  readonly registry: TileContentRegistry
  readonly cache: TileCache
  readonly scheduler: TileScheduler
  private readonly _selector = new TileSelector()
  private readonly _parser = new TilesetJsonParser()
  private readonly _tasks = new Map<string, AbortController>()
  private _reservationSerial = 0
  private readonly _probeHints = new TileProbeHints()
  private _probeRound = 0
  private _probeGeneration = 0
  private readonly _priorities = new Map<string, number>()
  private readonly _budgetController: TileBudgetController
  private readonly _failed = new Map<string, TilesDiagnostic>()
  private readonly _budgetRequired = new Map<string, number>()
  private readonly _budgetRequiredMemory = new Map<string, TileMemoryBytes>()
  private readonly _costHistory = new TileCostHistory()
  private _ready = new WeakSet<TileContentPackage>()
  private readonly _unregisterPreparation?: () => void
  private readonly _unsubscribeCapacity?: () => void
  private readonly _preparing = new Set<TileContentPackage>()
  private readonly _outputReservations = new Map<TileContentPackage, () => void>()
  private readonly _counters = { fullReads: 0, prefixReads: 0, readBytes: 0 }
  private readonly _textureQuality?: TileTextureQualityController
  private _maxTextureSize = Infinity
  private readonly _qualityJobs = new Map<string, QualityJob>()
  private readonly _qualityTargets = new Map<string, number>()
  private readonly _qualityBlocked = new Map<
    string,
    { target: number; costs: TileResourceCosts; capacity: string }
  >()
  private readonly _qualityFailed = new Map<string, number>()
  private _qualityTimer?: ReturnType<typeof setTimeout>
  private _qualityLimitId = ''
  private readonly _pressureCaps = new Map<string, number>()
  private readonly _candidates = new Set<string>()
  private readonly _diagnostics: TilesDiagnostic[] = []
  private _options: TilesRuntimeOptions
  private _adaptiveBudget?: TileAdaptiveBudgetController
  private _budgetTimer?: ReturnType<typeof setTimeout>
  private _displayed: readonly RuntimeTileNode[] = []
  private _generation = 0
  private _disposed = false
  private _active = false
  private _needsUpdate = false
  private _demandKey = ''
  private _firstVisibleMs?: number
  private _loadToFirstVisibleMs?: number
  private _mountedAt?: number
  private readonly _recordGeneration = ++nextRecordGeneration
  private _commitVersion = 0
  private readonly _views = new Map<
    string,
    {
      displayed: readonly RuntimeTileNode[]
      selection?: TileSelectionResult
      committedSelection?: TileSelectionResult
      view?: TilesViewSnapshot
      snapshot?: TilesDisplaySnapshot
    }
  >()
  private _defaultViewId = ''
  private _selectionRound = 0
  private _collisionRequests: readonly TileSelectionRequest[] = []

  setCollisionRequests(requests: readonly TileSelectionRequest[]): void {
    this._collisionRequests = requests
  }
  getCollisionContent(node: RuntimeTileNode): TileContentPackage | undefined {
    return this.cache.peek(node.id) ?? this.cache.peek(`${node.id}#collision`)
  }
  getCollisionFailure(node: RuntimeTileNode): string | undefined {
    return this._failed.get(`${node.id}#collision`)?.message ?? this._failed.get(node.id)?.message
  }
  private requestId(request: TileSelectionRequest): string {
    return request.purpose === 'collision' && request.kind === 'content'
      ? `${request.node.id}#collision`
      : request.node.id
  }

  constructor(
    private readonly _assetId: string,
    document: TilesetDocument,
    sourceToRecord: Matrix4,
    private readonly _source: IAssetSource,
    private readonly _adapter: ITileContentAdapter,
    options: TilesRuntimeOptions,
    private readonly _invalidate: () => void,
    private readonly _startedAt = performance.now(),
    private readonly _pool?: TileCacheBudget,
    private readonly _preparation?: TilePreparationCoordinator,
    private readonly _pipelineDiagnostics = false
  ) {
    this._options = options
    const policy = _adapter.getTextureQualityPolicy?.()
    if (policy?.mode === 'adaptive') this._textureQuality = new TileTextureQualityController(policy)
    this._unregisterPreparation = _preparation?.register(this, () => {
      this._ready = new WeakSet()
      this.cancelQualityJobs()
      if (this._active && !this._disposed) this.wake()
    })
    this.hierarchy = new ExplicitHierarchyProvider(document, sourceToRecord, options)
    this.registry = new TileContentRegistry(
      (error) => this.report(error, 'dispose'),
      !!_preparation,
      () => {
        this.notifyBudgetCapacityChanged()
      }
    )
    this.cache = new TileCache(options.maxCacheBytes, this.registry, _pool)
    this._budgetController = new TileBudgetController(this.cache)
    this._unsubscribeCapacity = _pool?.onCapacityChanged(() => this.notifyBudgetCapacityChanged())
    this.scheduler = new TileScheduler(
      options,
      () => {
        if (this._active) this._invalidate()
      },
      (error) => this.report(error, 'dispose')
    )
  }

  setActive(active: boolean): void {
    if (this._disposed || this._active === active) return
    this._active = active
    this.stopBudgetTimer()
    clearTimeout(this._qualityTimer)
    this._qualityTimer = undefined
    if (!active) {
      this._generation++
      this._preparation?.cancelOwner(this)
      this.cancelQualityJobs()
      for (const release of this._outputReservations.values()) release()
      this._outputReservations.clear()
      for (const [id, controller] of this._tasks) {
        controller.abort()
        this.cache.cancelReservation(id)
      }
      this._tasks.clear()
      this._probeHints.clear()
      this._priorities.clear()
      this._budgetController.reset()
      this.hierarchy.invalidate()
      for (const node of this._displayed) this.cache.unpin(node.id, 'display')
      for (const id of this._candidates) this.cache.unpin(id, 'candidate')
      this._candidates.clear()
      this._displayed = []
      this._views.clear()
    } else {
      this.scheduleBudgetCheck()
      this.wake()
    }
  }

  /** 仅由显式恢复操作调用，避免永久资源错误自动形成重试循环。 */
  retryFailed(): void {
    if (this._disposed) return
    this._probeHints.clear()
    this._qualityFailed.clear()
    for (const [id, error] of this._failed) {
      const node = this.hierarchy.getNode(error.tileId ?? id)
      if (node?.hierarchyState === 'failed') this.hierarchy.setHierarchyState(node, 'unknown')
    }
    for (const [id, error] of this._failed)
      if (error.code !== 'budget-exceeded') this._failed.delete(id)
    this._budgetController.clearStops()
    if (this._active) this.wake()
  }

  configure(options: Partial<TilesRuntimeOptions>): void {
    const next = resolveTilesRuntimeOptions(options, this._options)
    // 验证成功后才改变策略；显式设置相同额度也表示宿主接管。
    if (options.maxCacheBytes !== undefined) this.configureAdaptiveBudget(false)
    if (
      (Object.keys(next) as Array<keyof TilesRuntimeOptions>).every(
        (key) => next[key] === this._options[key]
      )
    )
      return
    this._adaptiveBudget?.resetObservation()
    this._options = next
    this.scheduler.configure(this._options)
    this.hierarchy.configure(this._options)
    this._budgetController.retainFallbacks(
      this._displayed,
      this._options.maxCacheBytes * this._options.fallbackCacheRatio,
      (id) => {
        const content = this.cache.peek(id)
        return !!content && this._ready.has(content)
      }
    )
    this.cache.configure(this._options.maxCacheBytes)
    this._budgetController.clearStops()
    if (this._active) this.wake()
  }

  configureAdaptiveBudget(options: TilesAdaptiveBudgetOptions | false): void {
    const controller = options
      ? new TileAdaptiveBudgetController(options, this._options.maxCacheBytes)
      : undefined
    this.stopBudgetTimer()
    this._adaptiveBudget = controller
    this.scheduleBudgetCheck()
  }

  /** 全局上限改变需要一次重新选择，不借助 configure({}) 清空已有回退。 */
  notifyBudgetLimitChanged(): void {
    this._adaptiveBudget?.resetObservation()
    if (this._active && !this._disposed) this.wake()
  }

  private stopBudgetTimer(): void {
    if (this._budgetTimer !== undefined) clearTimeout(this._budgetTimer)
    this._budgetTimer = undefined
    this._adaptiveBudget?.resetObservation()
  }

  private scheduleBudgetCheck(): void {
    if (!this._active || this._disposed || !this._adaptiveBudget || this._budgetTimer !== undefined)
      return
    this._budgetTimer = setTimeout(() => {
      this._budgetTimer = undefined
      const controller = this._adaptiveBudget
      if (!controller || !this._active || this._disposed) return
      const stats = this.cache.getStats()
      const next = controller.evaluate(
        performance.now(),
        this._options.maxCacheBytes,
        stats.protectedBytes + stats.reservedBytes,
        this.getBudgetPressure(stats),
        this._tasks.size > 0 || this._preparing.size > 0
      )
      if (next !== undefined) {
        // 自动调额保留恢复阈值、失败峰值和候选租约，由下一帧按真实容量恢复。
        this._options = Object.freeze({ ...this._options, maxCacheBytes: next })
        this.cache.configure(next)
        this.wake()
      }
      this.scheduleBudgetCheck()
    }, this._adaptiveBudget.options.checkIntervalMs)
  }

  private getBudgetPressure(
    local = this.cache.getStats(),
    shared = this._pool?.getStats()
  ): TilesBudgetPressure {
    const selectedIds = new Set<string>()
    if (this._budgetController.stops.size)
      for (const state of this._views.values())
        for (const node of state.selection?.desired ?? []) selectedIds.add(node.id)
    let required = this._active ? this._budgetController.requiredCapacity(selectedIds) : undefined
    if (this._active)
      for (const [id, bytes] of this._budgetRequired)
        if (
          this._priorities.has(id) &&
          !this._tasks.has(id) &&
          this._failed.get(id)?.code === 'budget-exceeded'
        )
          required = Math.min(required ?? Infinity, bytes)
    const requiredBytes = Math.max(0, required ?? 0)
    const localAvailableBytes =
      this._options.maxCacheBytes - local.protectedBytes - local.reservedBytes
    const sharedAvailableBytes = shared
      ? shared.maxBytes - shared.protectedBytes - shared.reservedBytes
      : null
    const localLimited = requiredBytes > localAvailableBytes
    const memory = this._pool?.getMemoryStats()
    const memoryLimit = memory?.limit
    const sharedMemoryLimited =
      !!memoryLimit &&
      [...this._budgetRequiredMemory].some(
        ([id, requiredMemory]) =>
          this._priorities.has(id) &&
          !this._tasks.has(id) &&
          this._failed.get(id)?.code === 'budget-exceeded' &&
          (requiredMemory.cpuBytes >
            memoryLimit.cpuBytes - memory.protected.cpuBytes - memory.reserved.cpuBytes ||
            requiredMemory.gpuBytes >
              memoryLimit.gpuBytes - memory.protected.gpuBytes - memory.reserved.gpuBytes)
      )
    const sharedLimited = requiredBytes > (sharedAvailableBytes ?? Infinity) || sharedMemoryLimited
    return Object.freeze({
      reason: localLimited
        ? sharedLimited
          ? 'local-and-shared'
          : 'local'
        : sharedLimited
          ? 'shared'
          : 'none',
      requiredBytes,
      localAvailableBytes,
      sharedAvailableBytes,
    })
  }

  markMounted(): void {
    this._mountedAt ??= performance.now()
  }

  /** 后更新的 Record 释放共享容量时，让前面的受阻 Record 获得一次恢复帧。 */
  notifyBudgetCapacityChanged(): void {
    if (!this._active || this._disposed) return
    const capacity = this.availableCapacity()
    if (
      [...this._qualityJobs.values()].some((job) => job.ready) ||
      [...this._qualityBlocked.values()].some(
        (item) => item.capacity !== this.capacityIdentity()
      ) ||
      this._budgetController.canRestore(capacity, this.availableMemory()) ||
      [...this._budgetRequired].some(
        ([id, bytes]) =>
          this.canRestoreBudget(id, bytes, capacity) &&
          this._failed.get(id)?.code === 'budget-exceeded'
      )
    )
      this.wake()
  }

  update(view: TilesViewSnapshot, context: TileContentPrepareContext): void {
    this.updateViews([view], context, [view.viewId])
  }

  /** 隐藏/删除的 view 立即退出；其它 view 的租约不受手动目标帧影响。 */
  retainViews(viewIds: readonly string[]): void {
    for (const [id, state] of this._views) {
      if (viewIds.includes(id)) continue
      for (const node of state.displayed) this.cache.unpin(node.id, 'display')
      this._views.delete(id)
    }
    this._displayed = [...this._views.values()].flatMap((state) => state.displayed)
  }

  updateViews(
    views: readonly TilesViewSnapshot[],
    context: TileContentPrepareContext,
    drawViewIds: readonly string[]
  ): void {
    if (!this._active || this._disposed) return
    this._needsUpdate = false
    // 在首次成本估算/解码前读取设备上限；仅使用缓存能力，不逐帧查询 renderer/device。
    const deviceLimit =
      context.graphics.kind === 'webgl2'
        ? context.legacyRenderer?.capabilities?.maxTextureSize
        : context.graphics.getInfo().webgpu?.deviceLimits.maxTextureDimension2D
    this._maxTextureSize =
      typeof deviceLimit === 'number' && Number.isSafeInteger(deviceLimit) && deviceLimit > 0
        ? deviceLimit
        : Infinity
    const now = performance.now()
    const overrun = this._preparation?.getStatus().lastOverrun
    this._textureQuality?.observe(
      views,
      now,
      overrun && overrun.kind === 'upload' && overrun.ms > 8 ? overrun.bytes : undefined
    )
    this.commitQualityJobs(views)
    this._probeRound++
    if (this._probeGeneration !== this.hierarchy.generation) {
      this._probeHints.clear()
      this._probeGeneration = this.hierarchy.generation
    }
    const capacity = this.availableCapacity()
    const isReady = (id: string): boolean => {
      const content = this.cache.peek(id)
      return !!content && this._ready.has(content)
    }
    const availableMemory = this.availableMemory()
    this._budgetController.restore(capacity, isReady, availableMemory)
    for (const [id, bytes] of this._budgetRequired) {
      if (
        this.canRestoreBudget(id, bytes, capacity) &&
        this._failed.get(id)?.code === 'budget-exceeded'
      ) {
        this._failed.delete(id)
        this._budgetRequired.delete(id)
        this._budgetRequiredMemory.delete(id)
      }
    }
    this.retainViews(views.map((view) => view.viewId))
    this._defaultViewId = views[0]?.viewId ?? ''
    const selections = new Map<string, TileSelectionResult>()
    for (const view of views) {
      let state = this._views.get(view.viewId)
      if (!state) {
        state = { displayed: [] }
        this._views.set(view.viewId, state)
      }
      const select = () =>
        this._selector.select(
          this.hierarchy.root,
          view,
          this._options,
          isReady,
          (id) => {
            const error = this._failed.get(id)
            return !!error && error.code !== 'budget-exceeded'
          },
          state.displayed,
          {
            refinementStops: this._budgetController.stops,
            hysteresisRatio: this._options.lodHysteresis,
          }
        )
      let selection = select()
      if (!this._tasks.size && !this._preparing.size) {
        const limit = Math.min(
          this._options.maxCacheBytes,
          this._pool?.getStats().maxBytes ?? Infinity
        )
        const blocked = selection.requests
          .map((request) => ({
            id: this.requestId(request),
            bytes: this._budgetRequired.get(this.requestId(request)),
          }))
          .filter(
            (value): value is { id: string; bytes: number } =>
              value.bytes !== undefined && value.bytes <= limit
          )
          .sort((a, b) => a.bytes - b.bytes)
        // 预算下调后即使没有新请求，也要释放超过新上限的旧细节。
        if (
          (capacity < 0 || availableMemory.cpuBytes < 0 || availableMemory.gpuBytes < 0) &&
          !blocked.length
        )
          blocked.push({ id: '', bytes: 1 })
        if (
          blocked.length &&
          this._budgetController.relieve(
            selection,
            blocked[0].bytes,
            capacity,
            (id) => this.hierarchy.getNode(id),
            isReady,
            {
              required: this._budgetRequiredMemory.get(blocked[0].id) ?? {
                cpuBytes: 0,
                gpuBytes: 0,
              },
              available: availableMemory,
            },
            this.hierarchy.getNode(blocked[0].id)
          )
        )
          selection = select()
      }
      selections.set(view.viewId, selection)
      state.selection = selection
      state.view = view
    }
    // 轮换消费者的队列先后，覆盖请求先于同 view 的细化请求；相同内容只入队一次。
    const requests = new Map<string, TileSelectionRequest>()
    const ordered = [...selections.values()]
    const offset = this._selectionRound++ % Math.max(1, ordered.length)
    for (
      let rank = 0;
      rank < Math.max(0, ...ordered.map((value) => value.requests.length));
      rank++
    ) {
      for (let index = 0; index < ordered.length; index++) {
        const request = ordered[(index + offset) % ordered.length].requests[rank]
        if (request && !requests.has(request.node.id))
          requests.set(request.node.id, { ...request, priority: -(rank * ordered.length + index) })
      }
    }
    for (const request of this._collisionRequests) {
      if (
        request.kind === 'content' &&
        (requests.has(request.node.id) || this._tasks.has(request.node.id))
      ) {
        if (!requests.has(request.node.id))
          requests.set(request.node.id, { ...request, purpose: 'display' })
      } else requests.set(this.requestId(request), request)
    }
    const selection = {
      desired: [...selections.values()].flatMap((value) => value.desired),
      requests: [...requests.values()],
    }
    const demandKey = selection.desired.map((node) => node.id).join('|')
    if (this._demandKey !== demandKey) {
      this._adaptiveBudget?.resetObservation()
      this._demandKey = demandKey
    }
    const wanted = new Set(selection.requests.map((request) => this.requestId(request)))
    this._probeHints.retain(
      new Set(selection.requests.map((request) => request.node.id)),
      this._probeRound
    )
    this._priorities.clear()
    const priorities = new Map<AbortSignal, number>()
    for (const request of selection.requests) {
      this._priorities.set(this.requestId(request), request.priority)
      const controller = this._tasks.get(this.requestId(request))
      if (controller) priorities.set(controller.signal, request.priority)
    }
    this.scheduler.reprioritizeMany(priorities)
    for (const [id, controller] of this._tasks) {
      if (!wanted.has(id)) {
        controller.abort()
        this.cache.cancelReservation(id)
        this._tasks.delete(id)
        const node = this.hierarchy.getNode(id)
        if (node?.hierarchyState === 'pending') this.hierarchy.setHierarchyState(node, 'unknown')
      }
    }
    // 只有实际绘制的 view 才切换提交；低频视图继续持有旧显示租约。
    for (const view of views) {
      if (!drawViewIds.includes(view.viewId)) continue
      const state = this._views.get(view.viewId)!
      const selected = selections.get(view.viewId)!
      const previousIds = new Set(state.displayed.map((node) => node.id))
      const nextIds = new Set(selected.displayed.map((node) => node.id))
      for (const node of selected.displayed)
        if (!previousIds.has(node.id)) this.cache.pin(node.id, 'display')
      const items: TileRenderItem[] = []
      for (const node of selected.displayed) {
        const content = this.cache.get(node.id)
        if (content)
          items.push(
            Object.freeze({
              assetId: this._assetId,
              content,
              matrixWorld: new Matrix4()
                .multiplyMatrices(view.placement, node.sourceToRecord)
                .multiply(node.tileMatrix),
            })
          )
      }
      state.snapshot = Object.freeze({
        recordGeneration: this._recordGeneration,
        viewId: view.viewId,
        version: ++this._commitVersion,
        items: Object.freeze(items),
      })
      state.displayed = selected.displayed
      state.committedSelection = selected
      for (const id of previousIds) if (!nextIds.has(id)) this.cache.unpin(id, 'display')
    }
    this._displayed = [...this._views.values()].flatMap((state) => state.displayed)
    const nextIds = new Set(this._displayed.map((node) => node.id))
    this._budgetController.retainFallbacks(
      this._displayed,
      this._options.maxCacheBytes * this._options.fallbackCacheRatio,
      isReady
    )
    const protectedCandidates = new Set([...wanted, ...selection.desired.map((node) => node.id)])
    for (const selected of selections.values())
      for (const node of selected.displayed) protectedCandidates.add(node.id)
    for (const id of protectedCandidates) {
      if (!nextIds.has(id) && !this._candidates.has(id) && this.cache.pin(id, 'candidate'))
        this._candidates.add(id)
    }
    for (const id of this._candidates) {
      if (!protectedCandidates.has(id) || nextIds.has(id)) {
        this.cache.unpin(id, 'candidate')
        this._candidates.delete(id)
      }
    }
    this.cache.evict()
    for (const [content, release] of this._outputReservations) {
      // 已失去需求且尚未开始准备的输出不能占住软水位，阻塞新的覆盖候选。
      if (!protectedCandidates.has(content.source.tileId) && !this._preparing.has(content))
        this.cache.releaseUnprotected(content.source.tileId)
      if (this.cache.peek(content.source.tileId) !== content) {
        release()
        this._outputReservations.delete(content)
      }
    }
    if (this._displayed.length && this._firstVisibleMs === undefined) {
      const now = performance.now()
      this._firstVisibleMs = now - (this._mountedAt ?? this._startedAt)
      this._loadToFirstVisibleMs = now - this._startedAt
    }
    for (const request of selection.requests) {
      const id = this.requestId(request)
      const target = this.qualityTarget(request.node, views)
      if (this._qualityTargets.get(id) !== target) {
        const task = this._tasks.get(id)
        if (task && this._qualityTargets.has(id)) {
          task.abort()
          this.cache.cancelReservation(id)
          this._tasks.delete(id)
        }
        if (this._failed.get(id)?.code === 'budget-exceeded') this._failed.delete(id)
        this._budgetRequired.delete(id)
        this._budgetRequiredMemory.delete(id)
        this._qualityTargets.set(id, target)
      }
      const content = this.cache.get(id)
      if (content && request.purpose !== 'collision') this.prepare(id, content, context)
      else if (!content && !this._tasks.has(id) && !this._failed.has(id)) this.start(request)
    }
    this.updateTextureQuality(views, context)
  }

  render(frame: ViewFrameContext, phase: RenderPhase, context: PhaseRenderContext): void {
    const snapshot = this.getDisplaySnapshot(frame.viewId)
    if (snapshot?.items.length) this._adapter.render(snapshot.items, frame, phase, context)
  }

  getDisplaySnapshot(viewId: string): TilesDisplaySnapshot | undefined {
    return this._active && !this._disposed ? this._views.get(viewId)?.snapshot : undefined
  }

  pick(query: PickQuery): PickResult | null {
    const snapshot = this.getDisplaySnapshot(query.viewId ?? this._defaultViewId)
    return snapshot ? (this._adapter.pick?.(snapshot, query) ?? null) : null
  }

  get activity(): Readonly<{
    immediate: boolean
    downloads: number
    decodes: number
    preparation: number
  }> {
    const stats = this.scheduler.getStats()
    return Object.freeze({
      immediate: this.hasImmediateWork,
      downloads: stats.downloads.active + stats.downloads.queued + stats.downloads.backoff,
      decodes: stats.decodes.active + stats.decodes.queued + stats.decodes.backoff,
      preparation: this._preparing.size + this._qualityJobs.size,
    })
  }

  get hasImmediateWork(): boolean {
    return (
      this._active &&
      (this._needsUpdate ||
        !!this._preparation?.hasImmediateWork(this) ||
        [...this._qualityJobs.values()].some((job) => this._preparation?.hasImmediateWork(job)))
    )
  }

  get status() {
    return this.getStatus(this._defaultViewId)
  }

  getStatus(viewId: string) {
    const stats = this.cache.getStats()
    const activity = this.activity
    const state = this._views.get(viewId)
    const selection = state?.selection
    const failures = [...this._failed]
      .filter(([id]) => {
        if (id.endsWith('#collision')) return false
        const node = this.hierarchy.getNode(id)
        return (
          !!node &&
          !!state?.view &&
          state.view.frustum.intersectsBox(
            node.traversalBounds.clone().applyMatrix4(state.view.placement)
          )
        )
      })
      .map(([, error]) => error)
    const shared = this._pool?.getStats()
    const sharedMemory = this._pool?.getMemoryStats()
    const memoryOverBudget =
      stats.budgetExceeded ||
      !!(shared && shared.totalBytes > shared.maxBytes) ||
      !!(
        sharedMemory?.limit &&
        (sharedMemory.committed.cpuBytes > sharedMemory.limit.cpuBytes ||
          sharedMemory.committed.gpuBytes > sharedMemory.limit.gpuBytes)
      )
    const budgetLimitedAdmission = failures.some((error) => error.code === 'budget-exceeded')
    const budgetLimitedSelection = selection?.budgetLimited ?? false
    const degraded = failures.length > 0 || memoryOverBudget || budgetLimitedSelection
    return {
      pipeline: this.getPipelineStatus(),
      hasCoverage: state?.committedSelection?.hasCoverage ?? false,
      quality:
        !this._active || !this._views.has(viewId)
          ? ('inactive' as const)
          : degraded
            ? ('degraded' as const)
            : selection?.sourceLimited
              ? ('source-limit' as const)
              : selection?.qualityLimited ||
                  !state?.snapshot ||
                  !selection ||
                  selection.displayed.length !== state.displayed.length ||
                  selection.displayed.some((node, index) => state.displayed[index] !== node)
                ? ('loading' as const)
                : ('ready' as const),
      displayedContents: this._views.get(viewId)?.snapshot?.items.length ?? 0,
      residentContents: stats.size,
      pendingRequests: activity.downloads,
      pendingDecodes: activity.decodes,
      pendingPreparation: activity.preparation,
      hierarchyPending: [...this._tasks.keys()].filter(
        (id) => !id.endsWith('#collision') && this.hierarchy.getNode(id)?.contentKind !== 'mesh'
      ).length,
      residentBytes: stats.residentBytes,
      transientBytes: stats.transientBytes + stats.reservedBytes,
      metadataBytes: this.hierarchy.metadataBytes,
      budgetExceeded: memoryOverBudget || budgetLimitedAdmission || budgetLimitedSelection,
      memoryOverBudget,
      budgetLimitedAdmission,
      budgetLimitedSelection,
      maxCacheBytes: this._options.maxCacheBytes,
      protectedBytes: stats.protectedBytes,
      reservedBytes: stats.reservedBytes,
      fallbackBytes: this._budgetController.fallbackBytes,
      stoppedRefinements: this._budgetController.stops.size,
      budgetPressure: this.getBudgetPressure(stats, shared),
      adaptiveBudget: this._adaptiveBudget?.options ?? (false as const),
      diagnostics: Object.freeze([...this._diagnostics]),
      firstVisibleMs: this._firstVisibleMs,
      loadToFirstVisibleMs: this._loadToFirstVisibleMs,
    }
  }

  getPipelineStatus(): TilesPipelineStatus {
    const image = this._adapter.getImageStatus?.()
    const preparation = this._preparation?.getStatus() ?? null
    const output = this._preparation?.output.getStatus() ?? null
    const activeBlocked = [...this._budgetRequiredMemory].filter(
      ([id]) => this._priorities.has(id) && this._failed.get(id)?.code === 'budget-exceeded'
    )
    const required = activeBlocked.reduce(
      (max, [, value]) => ({
        cpuBytes: Math.max(max.cpuBytes, value.cpuBytes),
        gpuBytes: Math.max(max.gpuBytes, value.gpuBytes),
      }),
      { cpuBytes: 0, gpuBytes: 0 }
    )
    const reasons: TilesPipelineStatus['reasons'][number][] = []
    if (activeBlocked.length) reasons.push('capacity')
    if (this._qualityBlocked.size) reasons.push('quality-limited')
    if (output?.queued) reasons.push('output-capacity')
    if (this.activity.downloads) reasons.push('network')
    if (image?.active || image?.queued) reasons.push('image')
    if (preparation?.waiting) reasons.push('shader')
    if (preparation?.contextLost) reasons.push('context-lost')
    if (
      [...this._failed].some(
        ([id, error]) => this._priorities.has(id) && error.code !== 'budget-exceeded'
      )
    )
      reasons.push('resource-error')
    return Object.freeze({
      preparationMode: this._adapter.createPreparation ? 'incremental' : 'legacy',
      image: Object.freeze({
        path: image?.path ?? 'unknown',
        queued: image?.queued ?? null,
        active: image?.active ?? null,
        activePeakBytes: image?.activePeakBytes ?? null,
        workerCount: image?.workerCount ?? null,
        failures: image?.failures ?? null,
        workerProcessing: image?.workerProcessing ?? null,
      }),
      preparation,
      output,
      retirement: Object.freeze(this.registry.getDeferredCosts()),
      textureQuality: Object.freeze({
        mode: this._textureQuality ? 'adaptive' : 'fixed',
        levels: Object.freeze([
          ...new Set(
            this._displayed
              .map((node) => this.cache.peek(node.id)?.textureQuality?.maxTextureSize)
              .filter((size): size is number => size !== undefined)
          ),
        ]),
        pending: this._qualityJobs.size,
        blocked: this._qualityBlocked.size,
        failures: this._qualityFailed.size,
      }),
      blocked: Object.freeze({
        count: activeBlocked.length,
        required: Object.freeze(required),
        available: Object.freeze(this.availableMemory()),
        historySaturated: this._costHistory.saturated,
      }),
      reasons: Object.freeze(reasons),
      counters: this._pipelineDiagnostics ? Object.freeze({ ...this._counters }) : null,
      actualMemoryBytes: null,
      gpuTimeMs: null,
    })
  }

  private qualityTarget(node: RuntimeTileNode, views: readonly TilesViewSnapshot[]): number {
    const memory = this._pool?.getMemoryStats()
    const limitId = `${memory?.limit?.cpuBytes}:${memory?.limit?.gpuBytes}:${this._options.maxCacheBytes}:${this._pool?.getStats().maxBytes}`
    if (limitId !== this._qualityLimitId) {
      this._qualityLimitId = limitId
      this._pressureCaps.clear()
    }
    const pressure = memory?.limit
      ? Math.max(
          (memory.committed.cpuBytes - memory.reserved.cpuBytes) / memory.limit.cpuBytes,
          (memory.committed.gpuBytes - memory.reserved.gpuBytes) / memory.limit.gpuBytes
        )
      : 0
    const target =
      this._textureQuality?.choose(node, views, performance.now(), pressure) ??
      this._adapter.getTextureQualityPolicy?.().maxTextureSize ??
      1024
    if (
      this._textureQuality &&
      pressure >= 0.8 &&
      (this.cache.peek(node.id)?.textureQuality?.maxTextureSize ?? 0) > target
    )
      this._pressureCaps.set(node.id, target)
    return Math.min(target, this._maxTextureSize, this._pressureCaps.get(node.id) ?? Infinity)
  }

  private capacityIdentity(): string {
    const memory = this.availableMemory()
    return `${this.availableCapacity()}:${memory.cpuBytes}:${memory.gpuBytes}`
  }

  private updateTextureQuality(
    views: readonly TilesViewSnapshot[],
    context: TileContentPrepareContext
  ): void {
    if (
      !this._textureQuality ||
      !this._adapter.updateTextureQuality ||
      !this._adapter.estimateTextureQuality ||
      !this._adapter.createPreparation ||
      !this._preparation
    )
      return
    const now = performance.now()
    const visible = new Map(this._displayed.map((node) => [node.id, node]))
    for (const job of this._qualityJobs.values()) {
      const node = visible.get(job.id)
      if (!node || this.qualityTarget(node, views) !== job.target) {
        job.controller.abort()
        this._preparation.cancelOwner(job)
        if (job.ready) this.finishQualityJob(job)
      }
    }
    for (const [id, node] of visible) {
      const content = this.cache.peek(id)
      if (!content?.textureQuality || this._qualityJobs.has(id) || !this._ready.has(content))
        continue
      const target = this.qualityTarget(node, views)
      if (target === content.textureQuality.maxTextureSize) {
        this._qualityBlocked.delete(id)
        continue
      }
      if (
        target > content.textureQuality.maxTextureSize &&
        !this._textureQuality.canUpgrade(id, now)
      )
        continue
      if (this._qualityFailed.get(id) === target) continue
      const blocked = this._qualityBlocked.get(id)
      if (blocked?.target === target && blocked.capacity === this.capacityIdentity()) continue
      this.startQualityJob(id, node, content, target, context)
    }
    for (const id of this._qualityBlocked.keys())
      if (!visible.has(id)) this._qualityBlocked.delete(id)
    for (const map of [this._qualityTargets, this._qualityFailed, this._pressureCaps])
      for (const id of map.keys())
        if (!visible.has(id) && !this._priorities.has(id) && !this._tasks.has(id)) map.delete(id)
    clearTimeout(this._qualityTimer)
    this._qualityTimer = undefined
    const next = this._textureQuality.nextCheck(now)
    if (next !== undefined && this._active)
      this._qualityTimer = setTimeout(
        () => {
          this._qualityTimer = undefined
          if (this._active && !this._disposed) this.wake()
        },
        Math.max(1, next)
      )
  }

  private startQualityJob(
    id: string,
    node: RuntimeTileNode,
    content: TileContentPackage,
    target: number,
    context: TileContentPrepareContext
  ): void {
    let costs = this._adapter.estimateTextureQuality!(content, target)
    const reservationId = `${id}:quality:${++this._reservationSerial}`
    if (!this.cache.reserve(reservationId, costs).accepted) {
      this._qualityBlocked.set(id, { target, costs, capacity: this.capacityIdentity() })
      return
    }
    this._qualityBlocked.delete(id)
    const lease = this.registry.retain(id)
    if (!lease) {
      this.cache.cancelReservation(reservationId)
      return
    }
    const job: QualityJob = {
      id,
      reservationId,
      controller: new AbortController(),
      content,
      lease,
      target,
      generation: this._generation,
      rendererGeneration: this._preparation!.generation,
      version: content.textureQuality?.version ?? 0,
      ready: false,
      finished: false,
    }
    this._qualityJobs.set(id, job)
    const pending = new Set<Promise<unknown>>()
    void (async () => {
      job.releaseOutput = await this._preparation!.output.acquire(
        Math.ceil(costs.cpuTextureBytes ?? costs.textureBytes),
        job.controller.signal
      )
      throwIfTilesAborted(job.controller.signal)
      job.update = await this._adapter.updateTextureQuality!(content, target, {
        source: content.source,
        resource: node.description.content!.context,
        signal: job.controller.signal,
        maxTextureSize: target,
        reserveCosts: (next) => {
          throwIfTilesAborted(job.controller.signal)
          costs = next
          if (!this.cache.reserve(reservationId, next).accepted)
            throw new TilesError({
              code: 'budget-exceeded',
              stage: 'decode',
              message: '新旧图片并存峰值超出容量',
            })
        },
        read: (uri, options = {}) =>
          this.read(uri, node.description.content!.context, this._priorities.get(id) ?? 0, {
            ...options,
            signal: job.controller.signal,
            prioritySignal: job.controller.signal,
          }),
        trackDecode: (promise) => {
          pending.add(promise)
          void promise.then(
            () => pending.delete(promise),
            () => pending.delete(promise)
          )
        },
      })
      throwIfTilesAborted(job.controller.signal)
      await new Promise<void>((resolve, reject) => {
        const version = {
          renderer: job.rendererGeneration,
          content: job.generation,
          quality: job.version + 1,
        }
        const task = this._adapter.createPreparation!(job.update!.candidate, context, version)
        this._preparation!.enqueue(
          job,
          task,
          () => this.wake(),
          (error) => (error ? reject(error) : resolve()),
          () => -4
        )
        this.wake()
      })
      throwIfTilesAborted(job.controller.signal)
      if (
        job.generation !== this._generation ||
        job.rendererGeneration !== this._preparation!.generation ||
        this.cache.peek(id) !== content
      )
        throw new TilesError({ code: 'cancelled', stage: 'runtime', message: '图片质量版本已失效' })
      job.ready = true
      this.wake()
    })().catch(async (error: unknown) => {
      await Promise.allSettled([...pending])
      this.finishQualityJob(job)
      if (!job.controller.signal.aborted && !this._disposed) {
        const diagnostic = toTilesDiagnostic(error, { ...content.source, stage: 'decode' })
        if (diagnostic.code === 'budget-exceeded')
          this._qualityBlocked.set(id, { target, costs, capacity: this.capacityIdentity() })
        else if (diagnostic.code !== 'cancelled') {
          this._qualityFailed.set(id, target)
          this.report(diagnostic)
        }
      }
    })
  }

  private commitQualityJobs(views: readonly TilesViewSnapshot[]): void {
    for (const job of [...this._qualityJobs.values()]) {
      if (!job.ready || !job.update) continue
      const node = this.hierarchy.getNode(job.id)
      if (
        job.controller.signal.aborted ||
        !node ||
        job.generation !== this._generation ||
        job.rendererGeneration !== this._preparation?.generation ||
        this.cache.peek(job.id) !== job.content ||
        job.content.textureQuality?.version !== job.version ||
        this.qualityTarget(node, views) !== job.target
      ) {
        this.finishQualityJob(job)
        continue
      }
      const committed = this.cache.commitCosts(job.id, job.reservationId, job.update.costs, () => {
        const previous = job.update!.commit()
        if (previous) this.registry.discard(previous)
      })
      if (!committed) {
        const costs = job.update.costs
        this.finishQualityJob(job)
        this._qualityBlocked.set(job.id, {
          target: job.target,
          costs,
          capacity: this.capacityIdentity(),
        })
        continue
      }
      this._textureQuality?.changed(job.id, performance.now())
      this._qualityFailed.delete(job.id)
      this.finishQualityJob(job)
    }
  }

  private finishQualityJob(job: QualityJob): void {
    if (job.finished) return
    job.finished = true
    if (this._qualityJobs.get(job.id) === job) this._qualityJobs.delete(job.id)
    try {
      job.update?.dispose()
    } catch (error) {
      this.report(error, 'dispose')
    } finally {
      job.releaseOutput?.()
      job.lease.release()
      this.cache.cancelReservation(job.reservationId)
      if (this._active && !this._disposed) this.wake()
    }
  }

  private cancelQualityJobs(force = false): void {
    for (const job of [...this._qualityJobs.values()]) {
      job.controller.abort()
      this._preparation?.cancelOwner(job, force)
      if (job.ready) this.finishQualityJob(job)
    }
  }

  dispose(): void {
    if (this._disposed) return
    this.setActive(false)
    this._probeHints.clear()
    this._disposed = true
    this.cancelQualityJobs(true)
    this._unregisterPreparation?.()
    this._unsubscribeCapacity?.()
    this.scheduler.dispose()
    try {
      this._adapter.setAppearance?.(this._assetId, undefined)
    } catch (error) {
      this.report(error, 'dispose')
    }
    this.cache.dispose()
    this.registry.close()
  }

  private start(request: TileSelectionRequest): void {
    const { node, kind } = request
    const id = this.requestId(request)
    const priority = () => this._priorities.get(id) ?? request.priority
    const ref = node.description.content!
    const controller = new AbortController()
    const generation = this._generation
    const qualityTarget = this._qualityTargets.get(id)
    const retainedId = `${id}:decode:${++this._reservationSerial}`
    const pendingDecodes = new Set<Promise<unknown>>()
    let retained = false
    const retainActiveReservation = () => {
      if (retained || !pendingDecodes.size) return
      this.cache.transferReservation(id, retainedId)
      retained = true
    }
    const trackDecode = (pending: Promise<unknown>, signal: AbortSignal) => {
      pendingDecodes.add(pending)
      signal.addEventListener('abort', retainActiveReservation, { once: true })
      if (signal.aborted || controller.signal.aborted) retainActiveReservation()
      const finished = () => {
        signal.removeEventListener('abort', retainActiveReservation)
        pendingDecodes.delete(pending)
        if (retained && !pendingDecodes.size) this.cache.cancelReservation(retainedId)
      }
      void pending.then(finished, finished)
    }
    controller.signal.addEventListener('abort', retainActiveReservation, { once: true })
    const hierarchyGeneration = this.hierarchy.generation
    this._tasks.set(id, controller)
    const valid = () =>
      !this._disposed &&
      this._active &&
      generation === this._generation &&
      hierarchyGeneration === this.hierarchy.generation &&
      this._tasks.get(id) === controller &&
      !controller.signal.aborted
    if (kind !== 'content') this.hierarchy.setHierarchyState(node, 'pending', hierarchyGeneration)
    // 解析失败时保留原始 URI，诊断路径不得再次调用可能抛错的数据源。
    let sourceUrl = ref.uri
    const execute = async (): Promise<void> => {
      sourceUrl = this._source.resolve(ref.uri, ref.context)
      const hintIdentity = JSON.stringify([
        generation,
        hierarchyGeneration,
        ref.id,
        sourceUrl,
        ref.context.documentUrl,
        Object.entries(ref.context.headers ?? {}).sort(),
        ref.context.credentials,
        ref.context.credentialOrigins,
      ])
      if (kind !== 'content') {
        const probeLimit = Math.min(this._options.maxProbeBytes, this._options.maxMetadataBytes)
        let data = await this.read(ref.uri, ref.context, priority(), {
          signal: controller.signal,
          prioritySignal: controller.signal,
          maxBytes: Math.min(16, probeLimit),
          prefix: true,
        })
        let classification = classifyTileResource(data)
        // GLB 只读容器头便停止；JSON 在独立探测额度内获得完整结构。
        if (
          classification.kind === 'unknown' &&
          !data.complete &&
          data.data.byteLength < probeLimit
        ) {
          data = await this.read(ref.uri, ref.context, priority(), {
            signal: controller.signal,
            prioritySignal: controller.signal,
            maxBytes: probeLimit,
            prefix: true,
          })
          classification = classifyTileResource(data)
        }
        if (classification.kind === 'unknown')
          throw new TilesError({
            code: 'limit-exceeded',
            stage: 'hierarchy',
            message: '有界探测未能确定内容类型，范围仍未验证',
          })
        if (classification.kind === 'tileset' && !data.complete) {
          data = await this.read(ref.uri, ref.context, priority(), {
            signal: controller.signal,
            prioritySignal: controller.signal,
            maxBytes: this._options.maxMetadataBytes,
          })
          classification = classifyTileResource(data)
        }
        if (!valid()) return
        if (classification.kind === 'tileset') {
          const context = Object.freeze({ ...ref.context, documentUrl: data.url })
          const document = this._parser.parse(classification.json, context, {
            maxDepth: this._options.maxHierarchyDepth,
            byteLength: data.data.byteLength,
          })
          this.hierarchy.installExternal(node, document, hierarchyGeneration, controller.signal)
        } else if (classification.kind === 'mesh') {
          this.hierarchy.setClassification(node, 'mesh', hierarchyGeneration)
          const length = glbByteLength(data)
          if (length !== undefined && data.url === sourceUrl)
            this._probeHints.remember(node.id, hintIdentity, length, this._probeRound)
        } else
          throw new TilesError({
            code: 'unsupported-content',
            stage: 'hierarchy',
            message: 'M0 无法识别或不支持该内容容器',
          })
        return
      }
      let reservedCosts: TileResourceCosts = {
        inputBytes: 0,
        geometryBytes: 0,
        textureBytes: 0,
        transientBytes: 0,
      }
      const costIdentity = JSON.stringify([
        this._recordGeneration,
        ref.id,
        sourceUrl,
        ref.context.documentUrl,
        Object.entries(ref.context.headers ?? {}).sort(),
        ref.context.credentials,
        ref.context.credentialOrigins,
        request.purpose ?? 'display',
        qualityTarget,
      ])
      const knownCosts = this._costHistory.get(costIdentity)
      if (!knownCosts && this._costHistory.saturated)
        throw new TilesError({
          code: 'budget-exceeded',
          stage: 'decode',
          message: '成本记录容量已满，等待加载世代更新',
        })
      const reserve = (requestedCosts: TileResourceCosts): void => {
        // 历史峰值在本次完整读取前预约，输入预约不能把它降回仅输入大小。
        const requestedMemory = tileMemoryCosts(requestedCosts)
        const knownMemory = knownCosts && tileMemoryCosts(knownCosts)
        const costs =
          knownCosts && knownMemory && (knownCosts.memory || requestedCosts.memory)
            ? {
                ...requestedCosts,
                memory: {
                  cpuBytes: Math.max(requestedMemory.cpuBytes, knownMemory.cpuBytes),
                  gpuBytes: Math.max(requestedMemory.gpuBytes, knownMemory.gpuBytes),
                },
              }
            : knownCosts && tileCostBytes(knownCosts) > tileCostBytes(requestedCosts)
              ? knownCosts
              : requestedCosts
        throwIfTilesAborted(controller.signal)
        if (!valid())
          throw new TilesError({ code: 'cancelled', stage: 'decode', message: '内容世代已失效' })
        const result = this.cache.reserve(id, costs)
        if (!result.accepted) {
          this._costHistory.remember(costIdentity, costs)
          this._budgetRequired.set(id, tileCostBytes(costs))
          this._budgetRequiredMemory.set(id, tileMemoryCosts(costs))
          throw new TilesError({
            code: 'budget-exceeded',
            stage: 'decode',
            message: `内容预算不足: ${result.reason}`,
          })
        }
        reservedCosts = costs
      }
      if (knownCosts) reserve(knownCosts)
      const readOptions = {
        signal: controller.signal,
        prioritySignal: controller.signal,
      }
      const reserveInput = (length: number): void =>
        reserve({ inputBytes: length, geometryBytes: 0, textureBytes: 0, transientBytes: length })
      const readOriginal = async (): Promise<TileResourceData> => {
        let data = await this.read(ref.uri, ref.context, priority(), {
          ...readOptions,
          maxBytes: Math.min(16, this._options.maxProbeBytes, this._options.maxCacheBytes),
          prefix: true,
        })
        let totalBytes = data.complete ? data.data.byteLength : data.totalBytes
        if (
          totalBytes === undefined &&
          data.data.byteLength >= 12 &&
          new DataView(data.data).getUint32(0, true) === 0x46546c67
        )
          totalBytes = new DataView(data.data).getUint32(8, true)
        // 没有长度信息时仍按原路径保守预约输入峰值。
        const inputLimit = totalBytes ?? Math.floor(this._options.maxCacheBytes / 2)
        reserveInput(inputLimit)
        if (!data.complete)
          data = await this.read(ref.uri, ref.context, priority(), {
            ...readOptions,
            maxBytes: inputLimit,
          })
        return data
      }
      const hint = this._probeHints.take(node.id, hintIdentity, this._probeRound)
      let data: TileResourceData | undefined
      if (hint !== undefined) {
        try {
          reserveInput(hint)
          const full = await this.read(ref.uri, ref.context, priority(), {
            ...readOptions,
            maxBytes: hint,
          })
          if (full.complete && full.url === sourceUrl && glbByteLength(full) === hint) data = full
        } catch (error) {
          // 仅提示可能过时的两类错误回退；网络/认证/取消沿用原错误处理。
          if (
            !(error instanceof TilesError) ||
            !['budget-exceeded', 'limit-exceeded'].includes(error.diagnostic.code)
          )
            throw error
        }
        if (!valid()) return
        if (!data) {
          this.cache.cancelReservation(id)
          this._budgetRequired.delete(id)
          this._budgetRequiredMemory.delete(id)
        }
      }
      data ??= await readOriginal()
      if (!valid()) return
      const estimatedCosts = this._adapter.estimate?.(data, request.purpose, qualityTarget) ?? {
        inputBytes: data.data.byteLength,
        geometryBytes: 0,
        textureBytes: 0,
        transientBytes: data.data.byteLength,
      }
      reserve(estimatedCosts)
      let releaseOutput =
        request.purpose !== 'collision' && this._preparation
          ? await this._preparation.output.acquire(
              Math.ceil(
                estimatedCosts.geometryBytes +
                  (estimatedCosts.cpuTextureBytes ?? estimatedCosts.textureBytes)
              ),
              controller.signal
            )
          : undefined
      try {
        throwIfTilesAborted(controller.signal)
        const externalInputs = new Map<string, number>()
        const readDependency = async (
          uri: string,
          signal: AbortSignal,
          options: TileReadOptions = {}
        ): Promise<TileResourceData> => {
          const context = { ...ref.context, documentUrl: data.url }
          const limit = Math.min(options.maxBytes ?? Infinity, this._options.maxCacheBytes)
          let dependency = await this.read(uri, context, priority(), {
            signal,
            prioritySignal: controller.signal,
            prefix: true,
            maxBytes: Math.min(16, limit),
          })
          const bytes = dependency.complete
            ? dependency.data.byteLength
            : (dependency.totalBytes ?? Math.floor(limit / 2))
          externalInputs.set(
            dependency.url,
            Math.max(externalInputs.get(dependency.url) ?? 0, bytes)
          )
          const inputBytes = Math.max(
            reservedCosts.inputBytes,
            data.data.byteLength +
              [...externalInputs.values()].reduce((sum, value) => sum + value, 0)
          )
          reserve({
            ...reservedCosts,
            memory: reservedCosts.memory && {
              cpuBytes: reservedCosts.memory.cpuBytes + inputBytes - reservedCosts.inputBytes,
              gpuBytes: reservedCosts.memory.gpuBytes,
            },
            inputBytes,
            transientBytes: reservedCosts.transientBytes + inputBytes - reservedCosts.inputBytes,
          })
          if (!dependency.complete)
            dependency = await this.read(uri, context, priority(), {
              ...options,
              signal,
              prioritySignal: controller.signal,
              maxBytes: Math.min(limit, bytes),
            })
          return dependency
        }
        const content = await this.scheduler.schedule(
          `${id}:${generation}`,
          'decode',
          priority(),
          (signal) =>
            this._adapter.decode(data, {
              maxTextureSize: qualityTarget,
              purpose: request.purpose,
              source: {
                assetId: this._assetId,
                tileId: node.id,
                contentId: ref.id,
                sourceUrl: data.url,
              },
              resource: { ...ref.context, documentUrl: data.url },
              signal,
              reserveCosts: reserve,
              trackDecode: (pending) => trackDecode(pending, signal),
              read: (uri, options = {}) => readDependency(uri, signal, options),
            }),
          {
            signal: controller.signal,
            prioritySignal: controller.signal,
            cleanup: (late) => this.registry.discard(late),
          }
        )
        if (!valid()) {
          this.registry.discard(content)
          return
        }
        const finalBytes = tileCostBytes(content.costs)
        const result = this.cache.admit(id, content)
        if (!result.accepted) {
          if (result.reason === 'oversized' || result.reason === 'protected-budget') {
            this._costHistory.remember(costIdentity, content.costs)
            this._budgetRequired.set(id, finalBytes)
            this._budgetRequiredMemory.set(id, tileMemoryCosts(content.costs))
          }
          throw new TilesError({
            code: 'budget-exceeded',
            stage: 'runtime',
            message: `内容缓存拒绝: ${result.reason}`,
          })
        }
        this.cache.pin(id, 'candidate')
        this._candidates.add(id)
        if (releaseOutput) {
          this._outputReservations.set(content, releaseOutput)
          releaseOutput = undefined
        }
      } finally {
        releaseOutput?.()
      }
    }
    void execute()
      .catch((error: unknown) => {
        if (!valid()) return
        const diagnostic = toTilesDiagnostic(error, {
          assetId: this._assetId,
          tileId: node.id,
          contentId: ref.id,
          sourceUrl,
        })
        if (diagnostic.code !== 'cancelled') {
          this._failed.set(id, diagnostic)
          this.report(diagnostic)
          if (kind !== 'content')
            this.hierarchy.setHierarchyState(node, 'failed', hierarchyGeneration)
        }
      })
      .finally(() => {
        controller.signal.removeEventListener('abort', retainActiveReservation)
        // 旧任务不得撤掉相同节点新任务的预约或状态。
        if (this._tasks.get(id) === controller) {
          this.cache.cancelReservation(id)
          this._tasks.delete(id)
          if (controller.signal.aborted && kind !== 'content' && node.hierarchyState === 'pending')
            this.hierarchy.setHierarchyState(node, 'unknown', hierarchyGeneration)
        }
        if (this._active && !this._disposed) this.wake()
      })
  }

  private read(
    uri: string,
    context: ResourceContext,
    priority: number,
    options: TileReadOptions & { prioritySignal?: AbortSignal }
  ): Promise<TileResourceData> {
    const url = this._source.resolve(uri, context)
    const key = JSON.stringify([
      url,
      Object.entries(context.headers ?? {}).sort(),
      context.credentials,
      context.credentialOrigins,
      options.maxBytes,
      options.prefix,
    ])
    return this.scheduler.schedule(
      key,
      'download',
      priority,
      async (signal) => {
        if (this._pipelineDiagnostics) {
          if (options.prefix) this._counters.prefixReads++
          else this._counters.fullReads++
        }
        const result = await this._source.read(url, context, { ...options, signal })
        if (this._pipelineDiagnostics) this._counters.readBytes += result.data.byteLength
        return result
      },
      {
        signal: options.signal,
        prioritySignal: options.prioritySignal,
        retryable: (error) => error instanceof TilesError && error.diagnostic.retryable === true,
      }
    )
  }

  private prepare(
    id: string,
    content: TileContentPackage,
    context: TileContentPrepareContext
  ): void {
    if (this._ready.has(content) || this._preparing.has(content) || this._failed.has(id)) return
    const lease = this.registry.retain(id)
    if (!lease) return
    this._preparing.add(content)
    const generation = this._generation
    const rendererGeneration = this._preparation?.generation
    const complete = (error?: unknown) => {
      try {
        if (
          this._disposed ||
          generation !== this._generation ||
          rendererGeneration !== this._preparation?.generation
        )
          return
        if (!error) {
          if (this.cache.peek(id) === content) this._ready.add(content)
        } else {
          const diagnostic = toTilesDiagnostic(error, {
            assetId: this._assetId,
            tileId: id,
            stage: 'decode',
          })
          if (diagnostic.code !== 'cancelled') {
            this._failed.set(id, diagnostic)
            this.report(diagnostic)
          }
        }
      } finally {
        this._preparing.delete(content)
        this._outputReservations.get(content)?.()
        this._outputReservations.delete(content)
        lease.release()
        if (this._active && !this._disposed) this.wake()
      }
    }
    if (this._preparation) {
      try {
        const version = { renderer: rendererGeneration!, content: generation, quality: 0 }
        let started = false
        const task = this._adapter.createPreparation?.(content, context, version) ?? {
          version,
          mode: 'legacy' as const,
          step: () => {
            if (started) return { state: 'ready' as const }
            started = true
            return {
              state: 'waiting' as const,
              completion: Promise.resolve(this._adapter.prepare?.(content, context)),
            }
          },
          cancel: () => {},
        }
        this._preparation.enqueue(
          this,
          task,
          () => this.wake(),
          complete,
          () => this._priorities.get(id) ?? 0
        )
      } catch (error) {
        complete(error)
      }
      return
    }
    // lease 跨越不可取消的 GPU 准备；hide/dispose 也不会提前释放底层对象。
    void Promise.resolve()
      .then(() => this._adapter.prepare?.(content, context))
      .then(() => complete(), complete)
  }

  private canRestoreBudget(id: string, bytes: number, capacity: number): boolean {
    if (bytes > capacity) return false
    const required = this._budgetRequiredMemory.get(id)
    const memory = this._pool?.getMemoryStats()
    if (!required || !memory?.limit) return true
    return (
      required.cpuBytes <=
        memory.limit.cpuBytes - memory.protected.cpuBytes - memory.reserved.cpuBytes &&
      required.gpuBytes <=
        memory.limit.gpuBytes - memory.protected.gpuBytes - memory.reserved.gpuBytes
    )
  }

  private availableCapacity(): number {
    const local = this.cache.getStats()
    const shared = this._pool?.getStats()
    return Math.min(
      this._options.maxCacheBytes - local.protectedBytes - local.reservedBytes,
      shared ? shared.maxBytes - shared.protectedBytes - shared.reservedBytes : Infinity
    )
  }

  private availableMemory(): TileMemoryBytes {
    const memory = this._pool?.getMemoryStats()
    return {
      cpuBytes: memory?.limit
        ? memory.limit.cpuBytes - memory.protected.cpuBytes - memory.reserved.cpuBytes
        : Infinity,
      gpuBytes: memory?.limit
        ? memory.limit.gpuBytes - memory.protected.gpuBytes - memory.reserved.gpuBytes
        : Infinity,
    }
  }

  private wake(): void {
    this._needsUpdate = true
    this._invalidate()
  }

  private report(error: unknown, stage: TilesDiagnostic['stage'] = 'runtime'): void {
    const diagnostic =
      error && typeof error === 'object' && 'code' in error
        ? (error as TilesDiagnostic)
        : toTilesDiagnostic(error, { stage, assetId: this._assetId })
    // 有界诊断历史，避免损坏的大型层次永久保留大量错误文本。
    const duplicate = this._diagnostics.findIndex(
      (previous) =>
        previous.code === diagnostic.code &&
        previous.stage === diagnostic.stage &&
        previous.tileId === diagnostic.tileId &&
        previous.sourceUrl === diagnostic.sourceUrl
    )
    if (duplicate !== -1) this._diagnostics.splice(duplicate, 1)
    this._diagnostics.push(diagnostic)
    if (this._diagnostics.length > 100) this._diagnostics.shift()
  }
}
